// First: Zod must not try eval under the Content Security Policy (zod-config.ts).
import './zod-config.ts';
import '@wodi/ui/styles.css';

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { api, ApiError } from './api/client.ts';
import { installAppearance } from './appearance/apply.ts';
import { sessionQuery } from './api/session.ts';
import { createErrorReporter, installGlobalErrorHandlers } from './errors/report.ts';
import { I18nProvider } from './i18n/i18n.tsx';
import { createAppRouter } from './router.tsx';

/** When the server says the session is gone, go to sign-in and keep where they were. */
function onApiError(error: unknown) {
  if (!(error instanceof ApiError) || error.code !== 'not_signed_in') return;
  const here = router.state.location;
  if (here.pathname.startsWith('/sign-in')) return;
  // The patient portal and feedback links have their own sign-in, never the staff one.
  if (here.pathname.startsWith('/portal') || here.pathname.startsWith('/f/')) return;
  queryClient.setQueryData(sessionQuery.queryKey, null);
  void router.navigate({ to: '/sign-in', search: { reason: 'expired', next: here.href } });
}

// The theme and mode last used on this device, until the session says otherwise (ADR 0030).
installAppearance();

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onApiError }),
  mutationCache: new MutationCache({ onError: onApiError }),
  defaultOptions: {
    queries: {
      // Retry only what might succeed on retry: dropped connections and server errors.
      retry: (count, error) =>
        count < 2 && (!(error instanceof ApiError) || error.status === 0 || error.status >= 500),
      refetchOnWindowFocus: false,
    },
  },
});

const router = createAppRouter(queryClient);

// Error tracking without patient data (ADR 0007): the route pattern, never the address.
const errorReporter = createErrorReporter({
  send: (report) => api('POST', '/client-errors', { body: report }),
  getRoute: () => router.state.matches.at(-1)?.fullPath,
});
installGlobalErrorHandlers(window, errorReporter);

const root = document.getElementById('root');
if (root === null) throw new Error('Missing #root element');

createRoot(root, {
  // Render errors, whether a route's error screen caught them or nothing did.
  onCaughtError: (error) => {
    errorReporter.report(error, 'render');
  },
  onUncaughtError: (error) => {
    errorReporter.report(error, 'render');
  },
}).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <RouterProvider router={router} />
      </I18nProvider>
    </QueryClientProvider>
  </StrictMode>,
);
